// The eCART Allure traceability taxonomy: the single data source for every eCART test
// project's annotations. The tree mirrors packages/ehr-ecart/docs/testTraceability.md, which
// mirrors the SRS/SDS in Confluence. Runtime annotation helpers live per test project
// (packages/ehr-ecart/test/helpers/allure.ts, e2e/ui-ehr-ecart-e2e/src/allure.ts).
const SRS_PAGE =
  'https://agilemd.atlassian.net/wiki/spaces/PMR/pages/194150543/AgileMD+eCARTv5+-+Software+Requirements+Specification+SRS';

export const ECART_TRACE_EPICS = {
  ACCESS_SESSION_MANAGEMENT: 'Access & Session Management',
  COMPONENT_PROCESSING: 'Component Processing',
  CONFIGURATION: 'Configuration',
  DATA_INGESTION_NORMALIZATION: 'Data Ingestion & Normalization',
  NOTIFICATION_BANNERS_SAFETY: 'Notification Banners (safety)',
  OUTBOUND_INTEGRATION: 'Outbound Integration',
  PATIENT_DETAIL_VIEW_LIVE: 'Patient Detail View (Live)',
  PATIENT_LIST_VIEW: 'Patient List View',
  RELIABILITY_PERFORMANCE: 'Reliability & Performance',
  RETROSPECTIVE_APPLICATION: 'Retrospective Application',
  SCORING_ENGINE: 'Scoring Engine',
  VERIFY_WORKFLOW_SAFETY: 'Verify Workflow (safety)'
} as const;

export const ECART_TRACE_FEATURES = {
  ABNORMALITY_INDICATORS: 'Abnormality indicators',
  ACTIVITY_FEED: 'Activity feed',
  AGE_DOB: 'Age & DOB',
  ALGORITHM_INPUT_EXPORT: 'Algorithm input export',
  BLOOD_GASES_LACTATE_EXPIRY: 'Blood gases & lactate expiry',
  BLOOD_PRESSURE_PAIRING: 'Blood pressure pairing',
  BRADEN_BRADEN_QD: 'Braden & Braden QD',
  CARRY_FORWARD_MISSINGNESS: 'Carry-forward & missingness',
  CASE_SETUP_METADATA_TAGGING: 'Case setup & metadata tagging',
  COMMENT_ENTRY: 'Comment entry',
  COMPONENT_LIST: 'Component list',
  COMPONENT_WEIGHTING: 'Component weighting',
  CONFIG_VALIDATION: 'Config validation',
  DERIVED_INPUTS: 'Derived inputs (SDS step 8)',
  DETERIORATION_ODDS: 'Deterioration odds',
  DISPOSITION: 'Disposition',
  DISPOSITION_BEHAVIOR_TIMEOUTS: 'Disposition behavior & timeouts',
  DISPOSITION_ENTRY: 'Disposition entry',
  DUPLICATE_DEDUP_HANDLING: 'Duplicate & dedup handling',
  EHR_CONTEXT_LAUNCH: 'EHR-context launch',
  ENCOUNTER_SEARCH: 'Encounter search',
  EXTREME_VALUE_CONFIRMATION: 'Extreme-value confirmation',
  FACILITY_UNIT_SCOPING: 'Facility & unit scoping',
  FHIR_STU3_CONFORMANCE: 'FHIR STU3 conformance',
  FILTER: 'Filter',
  FIO2_DERIVATION: 'FiO2 derivation',
  FRAME_GROUPING: 'Frame grouping',
  GLOBAL_NAVIGATION_BANNER: 'Global navigation banner',
  JOB_TRIGGERING_QUEUE_SEMANTICS: 'Job triggering & queue semantics',
  KIOSK_DE_IDENTIFIED_ACCESS: 'Kiosk & de-identified access',
  LIST_RENDERING_COLUMNS: 'List rendering & columns',
  LIVE_VS_HISTORICAL_CASE_PERSISTENCE: 'Live vs Historical case persistence',
  LOCATION_ATTRIBUTION_CLASSIFICATION: 'Location attribution & classification',
  MENTAL_STATUS_AVPU: 'Mental status & AVPU',
  MINI_WIDGET: 'Mini widget',
  MODEL_SELECTION_PMML_EXECUTION: 'Model selection & PMML execution',
  MODEL_VERSION_STAMPING: 'Model version stamping',
  NEWLY_HIGH_SCORE_NOTIFICATION: 'Newly-high-score notification',
  NEW_SCORE_COMMUNICATION_REQUEST: 'New-score CommunicationRequest',
  OBSERVATION_COLLECTION_RESCORE_FROM: 'Observation collection & rescoreFrom',
  OBSERVATION_STATUS_FILTERING: 'Observation status filtering',
  ORANGE_WARNING_BANNER: 'Orange warning banner (downtime, sub-18)',
  PAGE_COMPOSITION: 'Page composition',
  PATHWAY_CDS_LINKAGE: 'Pathway / CDS linkage',
  PRIVACY_MODE: 'Privacy mode',
  RESCORING_HISTORICAL_CORRECTIONS: 'Rescoring & historical corrections',
  RESPONSIVENESS_AVPU: 'Responsiveness (AVPU, isDisoriented)',
  RETROSPECTIVE_DETAIL_VIEW: 'Retrospective detail view',
  SCORE_PERSISTENCE: 'Score persistence',
  SCORE_PRESENTATION: 'Score presentation',
  SCORE_ROUNDING: 'Score rounding',
  SCORING_ELIGIBILITY_EXCLUSION: 'Scoring eligibility & exclusion',
  SCORING_LATENCY: 'Scoring latency',
  SEARCH: 'Search',
  SEARCH_RESULTS: 'Search results',
  SESSION_LIFECYCLE: 'Session lifecycle',
  SUBCOMPONENT_EXPANSION: 'Subcomponent expansion',
  THRESHOLD_CATEGORIZATION: 'Threshold categorization',
  TIME_BASED_INPUTS_TIMEZONE: 'Time-based inputs & timezone',
  TREND_CALCULATIONS: 'Trend calculations',
  TREND_DERIVATIONS: 'Trend derivations',
  TREND_INTERACTION: 'Trend interaction',
  TREND_LINE_INITIAL_VS_UPDATED: 'Trend line (initial vs updated)',
  UI_FEATURE_TOGGLES: 'UI feature toggles / facility & unit scoping',
  UNIT_CONVERSION_VALIDATION: 'Unit conversion & validation',
  USER_FACING_ERROR_NOTIFICATION: 'User-facing error notification',
  VERIFY_DESIGNATION_SCORE_SUPPRESSION: 'Verify designation & score suppression',
  VITALS_TEMPERATURE_BP_PAIRING: 'Vitals (temperature, BP pairing)',
  WBC_DIFFERENTIAL: 'WBC differential'
} as const;

export interface EcartTraceRequirement {
  id: string;
  /** null for SDS steps: they have no anchor on the SRS page, so they carry no link */
  url: string | null;
}

function srs(id: string): EcartTraceRequirement {
  return { id, url: `${SRS_PAGE}#${id}` };
}

function sds(step: string): EcartTraceRequirement {
  return { id: `SDS.${step}`, url: null };
}

export const ECART_TRACE_REQUIREMENTS = {
  FR_4_001: srs('FR.4.001'),
  FR_4_002: srs('FR.4.002'),
  FR_4_003: srs('FR.4.003'),
  FR_4_004: srs('FR.4.004'),
  FR_4_005: srs('FR.4.005'),
  FR_4_006: srs('FR.4.006'),
  FR_4_007: srs('FR.4.007'),
  FR_4_009: srs('FR.4.009'),
  FR_4_011: srs('FR.4.011'),
  FR_4_012: srs('FR.4.012'),
  FR_4_014: srs('FR.4.014'),
  FR_4_015: srs('FR.4.015'),
  FR_4_016: srs('FR.4.016'),
  FR_4_018: srs('FR.4.018'),
  FR_4_020: srs('FR.4.020'),
  FR_4_021: srs('FR.4.021'),
  FR_4_022: srs('FR.4.022'),
  FR_4_025: srs('FR.4.025'),
  FR_4_026: srs('FR.4.026'),
  FR_4_027: srs('FR.4.027'),
  FR_4_028: srs('FR.4.028'),
  FR_4_029: srs('FR.4.029'),
  FR_4_030: srs('FR.4.030'),
  FR_4_031: srs('FR.4.031'),
  FR_4_032: srs('FR.4.032'),
  FR_4_033: srs('FR.4.033'),
  FR_4_034: srs('FR.4.034'),
  FR_4_035: srs('FR.4.035'),
  FR_4_036: srs('FR.4.036'),
  FR_4_037: srs('FR.4.037'),
  FR_4_038: srs('FR.4.038'),
  FR_4_039: srs('FR.4.039'),
  PR_5_001: srs('PR.5.001'),
  PR_5_004: srs('PR.5.004'),
  PR_5_008: srs('PR.5.008'),
  PR_5_009: srs('PR.5.009'),
  PR_5_010: srs('PR.5.010'),
  PR_5_011: srs('PR.5.011'),
  SCR_7_002: srs('SCR.7.002'),
  SCR_7_003: srs('SCR.7.003'),
  SCR_7_004: srs('SCR.7.004'),
  SCR_7_019: srs('SCR.7.019'),
  SR_6_001: srs('SR.6.001'),
  SR_6_002: srs('SR.6.002'),
  SR_6_003: srs('SR.6.003'),
  SR_6_007: srs('SR.6.007'),
  UR_1_001: srs('UR.1.001'),
  UR_1_004: srs('UR.1.004'),
  UR_1_005: srs('UR.1.005'),
  UR_2_006: srs('UR.2.006'),
  UR_2_007: srs('UR.2.007'),
  UR_2_008: srs('UR.2.008'),
  UR_2_009: srs('UR.2.009'),
  UR_2_010: srs('UR.2.010'),
  UR_2_011: srs('UR.2.011'),
  UR_2_012: srs('UR.2.012'),
  UR_2_014: srs('UR.2.014'),
  UR_2_015: srs('UR.2.015'),
  UR_3_014: srs('UR.3.014'),
  UR_3_015: srs('UR.3.015'),
  UR_3_016: srs('UR.3.016'),
  UR_3_017: srs('UR.3.017'),
  UR_3_018: srs('UR.3.018'),
  UR_3_019: srs('UR.3.019'),
  UR_3_020: srs('UR.3.020'),
  UR_3_021: srs('UR.3.021'),
  UR_3_022: srs('UR.3.022'),
  UR_3_023: srs('UR.3.023'),
  UR_3_024: srs('UR.3.024'),
  UR_3_025: srs('UR.3.025'),
  UR_3_026: srs('UR.3.026'),
  UR_3_027: srs('UR.3.027'),
  UR_3_029: srs('UR.3.029'),
  UR_3_030: srs('UR.3.030'),
  UR_3_031: srs('UR.3.031'),
  UR_3_032: srs('UR.3.032'),
  UR_3_034: srs('UR.3.034'),
  UR_3_036: srs('UR.3.036'),
  UR_3_037: srs('UR.3.037'),
  UR_3_038: srs('UR.3.038'),
  UR_3_041: srs('UR.3.041'),
  UR_3_044: srs('UR.3.044'),
  UR_3_045: srs('UR.3.045'),
  UR_3_046: srs('UR.3.046'),
  UR_3_047: srs('UR.3.047'),
  SDS_4: sds('4'),
  SDS_6: sds('6'),
  SDS_7: sds('7')
} as const;

export const ECART_TRACE_SEVERITIES = {
  BLOCKER: 'blocker',
  CRITICAL: 'critical',
  NORMAL: 'normal',
  MINOR: 'minor'
} as const;
